'use client';

import React, { useState, useEffect, useMemo, useRef } from 'react';
import ChevronLeftIcon from '@/components/icons/ChevronLeftIcon';
import ChevronRightIcon from '@/components/icons/ChevronRightIcon';
import { Button, ModalCloseButton, ModalPortal } from '@/components';
import { useScrollLock } from '@/lib/use-scroll-lock';

export type DatePickerModalProps = {
  isOpen: boolean;
  value?: string; // YYYY-MM-DD
  onClose: () => void;
  onChange: (value: string) => void;
  title?: string;
  min?: string; // YYYY-MM-DD
  max?: string; // YYYY-MM-DD
  /** Year shown by default when no value is provided. Defaults to current year - 25. */
  defaultYear?: number;
};

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_LABELS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type Mode = 'days' | 'months' | 'years';

function parseIso(iso: string): { y: number; m: number; d: number } | null {
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return { y: parseInt(match[1], 10), m: parseInt(match[2], 10) - 1, d: parseInt(match[3], 10) };
}

/* Accepts either a bare YYYY-MM-DD or a full ISO timestamp (the backend
   sometimes returns `2000-01-15T00:00:00.000Z` for date-only fields).
   Returns the date-only form, or '' when unparseable. */
function normalizeDateOnly(input: string): string {
  if (!input) return '';
  const head = input.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(head) ? head : '';
}

function toIso(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function firstWeekday(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

export default function DatePickerModal({
  isOpen,
  value = '',
  onClose,
  onChange,
  title = 'Select date',
  min,
  max,
  defaultYear,
}: DatePickerModalProps) {
  useScrollLock(isOpen);

  const today = useMemo(() => {
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth(), d: n.getDate() };
  }, []);

  /* Pick the initial calendar view for the no-value case:
       1. explicit `defaultYear` (e.g. DOB at current-25, January)
       2. `min` boundary (e.g. delivery window starting today)
       3. today's month
     This keeps the modal landing on a useful month for each use case
     instead of always defaulting to January of an arbitrary year. */
  const fallbackView = useMemo(() => {
    if (defaultYear !== undefined) return { y: defaultYear, m: 0 };
    const minP = min ? parseIso(min) : null;
    if (minP) return { y: minP.y, m: minP.m };
    return { y: today.y, m: today.m };
  }, [defaultYear, min, today]);

  const normalizedValue = normalizeDateOnly(value);
  const [draft, setDraft] = useState<string>(normalizedValue);
  const [viewYear, setViewYear] = useState<number>(() => parseIso(normalizedValue)?.y ?? fallbackView.y);
  const [viewMonth, setViewMonth] = useState<number>(() => parseIso(normalizedValue)?.m ?? fallbackView.m);
  const [mode, setMode] = useState<Mode>('days');

  // Reset internal state every time the modal opens
  useEffect(() => {
    if (!isOpen) return;
    const next = normalizeDateOnly(value);
    const parsed = parseIso(next);
    setDraft(next);
    setViewYear(parsed?.y ?? fallbackView.y);
    setViewMonth(parsed?.m ?? fallbackView.m);
    setMode('days');
  }, [isOpen, value, fallbackView]);

  // Escape-to-close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Scroll the selected year into view when entering the years grid
  const yearsScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (mode !== 'years') return;
    const el = yearsScrollRef.current?.querySelector<HTMLElement>('[data-selected-year="true"]');
    el?.scrollIntoView({ block: 'center' });
  }, [mode]);

  if (!isOpen) return null;

  const minP = min ? parseIso(min) : null;
  const maxP = max ? parseIso(max) : null;
  const minTime = minP ? new Date(minP.y, minP.m, minP.d).getTime() : -Infinity;
  const maxTime = maxP ? new Date(maxP.y, maxP.m, maxP.d).getTime() : Infinity;

  const isDayDisabled = (y: number, m: number, d: number): boolean => {
    const t = new Date(y, m, d).getTime();
    return t < minTime || t > maxTime;
  };

  const isMonthDisabled = (y: number, m: number): boolean => {
    const lastDay = daysInMonth(y, m);
    const monthEnd = new Date(y, m, lastDay).getTime();
    const monthStart = new Date(y, m, 1).getTime();
    return monthEnd < minTime || monthStart > maxTime;
  };

  const isYearDisabled = (y: number): boolean => {
    const yearEnd = new Date(y, 11, 31).getTime();
    const yearStart = new Date(y, 0, 1).getTime();
    return yearEnd < minTime || yearStart > maxTime;
  };

  // ── Days view data ────────────────────────────────────────────────────────
  const totalDays = daysInMonth(viewYear, viewMonth);
  const lead = firstWeekday(viewYear, viewMonth);
  const cells: { day: number; iso: string; currentMonth: boolean; disabled: boolean }[] = [];
  for (let i = 0; i < lead; i++) {
    const pm = viewMonth === 0 ? 11 : viewMonth - 1;
    const py = viewMonth === 0 ? viewYear - 1 : viewYear;
    const pd = daysInMonth(py, pm) - lead + i + 1;
    cells.push({ day: pd, iso: toIso(py, pm, pd), currentMonth: false, disabled: isDayDisabled(py, pm, pd) });
  }
  for (let d = 1; d <= totalDays; d++) {
    cells.push({ day: d, iso: toIso(viewYear, viewMonth, d), currentMonth: true, disabled: isDayDisabled(viewYear, viewMonth, d) });
  }
  while (cells.length < 42) {
    const idx = cells.length - lead - totalDays + 1;
    const nm = viewMonth === 11 ? 0 : viewMonth + 1;
    const ny = viewMonth === 11 ? viewYear + 1 : viewYear;
    cells.push({ day: idx, iso: toIso(ny, nm, idx), currentMonth: false, disabled: isDayDisabled(ny, nm, idx) });
  }

  // ── Years view data ───────────────────────────────────────────────────────
  const yearsRangeStart = (minP?.y ?? today.y - 120);
  const yearsRangeEnd = (maxP?.y ?? today.y + 10);
  const yearList: number[] = [];
  for (let y = yearsRangeEnd; y >= yearsRangeStart; y--) yearList.push(y);

  const todayIso = toIso(today.y, today.m, today.d);
  const draftP = parseIso(draft);

  const goPrev = () => {
    if (mode === 'days') {
      if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
      else setViewMonth(viewMonth - 1);
    } else if (mode === 'months') {
      setViewYear(viewYear - 1);
    } else {
      setViewYear(clamp(viewYear - 12, yearsRangeStart, yearsRangeEnd));
    }
  };

  const goNext = () => {
    if (mode === 'days') {
      if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
      else setViewMonth(viewMonth + 1);
    } else if (mode === 'months') {
      setViewYear(viewYear + 1);
    } else {
      setViewYear(clamp(viewYear + 12, yearsRangeStart, yearsRangeEnd));
    }
  };

  const headerLabel =
    mode === 'days'
      ? `${MONTHS[viewMonth]} ${viewYear}`
      : mode === 'months'
        ? `${viewYear}`
        : 'Select year';

  const cycleMode = () => {
    setMode((m) => (m === 'days' ? 'months' : m === 'months' ? 'years' : 'days'));
  };

  const apply = () => {
    if (!draft) return;
    onChange(draft);
    onClose();
  };

  const clear = () => {
    setDraft('');
    onChange('');
    onClose();
  };

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[1000] flex items-end justify-center bg-[#07063CBF] p-4 sm:items-center"
        onClick={onClose}
        role="presentation"
      >
        <div
          className="relative flex w-full max-w-sm flex-col overflow-hidden rounded-2xl bg-constant-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-10 px-5 py-5">
            <h3 className="text-lg font-bold text-text-primary">{title}</h3>
            <ModalCloseButton
              onClick={onClose}
              className="absolute right-5 top-5 z-10"
              aria-label="Close"
            />
          </div>

          {/* Selected preview */}
          <div className="px-5 pt-4">
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">
                {draftP ? 'Selected' : 'No date selected'}
              </span>
              {draft && (
                <Button
                  type="button"
                  variant="text"
                  color="primary"
                  size="sm"
                  onClick={() => setDraft('')}
                  className="px-1"
                >
                  Reset
                </Button>
              )}
            </div>
            <p className="mt-0.5 text-base font-semibold text-text-primary">
              {draftP
                ? new Date(draftP.y, draftP.m, draftP.d).toLocaleDateString(undefined, {
                    weekday: 'short',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : '—'}
            </p>
          </div>

          {/* Calendar */}
          <div className="px-5 pt-3 pb-4">
            <div className="mb-3 flex items-center justify-between">
              <Button
                type="button"
                variant="icon"
                color="white"
                size="icon-md"
                onClick={goPrev}
                aria-label="Previous"
                className="!rounded-lg"
              >
                <ChevronLeftIcon size={18} color="currentColor" />
              </Button>
              <Button
                type="button"
                variant="text"
                color="primary"
                size="md"
                onClick={cycleMode}
                className="px-3 text-sm font-semibold"
                aria-label="Switch view"
              >
                {headerLabel}
              </Button>
              <Button
                type="button"
                variant="icon"
                color="white"
                size="icon-md"
                onClick={goNext}
                aria-label="Next"
                className="!rounded-lg"
              >
                <ChevronRightIcon size={18} color="currentColor" />
              </Button>
            </div>

            {mode === 'days' && (
              <>
                <div className="grid grid-cols-7 gap-1">
                  {WEEKDAYS.map((w) => (
                    <div key={w} className="py-1 text-center text-[11px] font-medium text-text-tertiary">
                      {w}
                    </div>
                  ))}
                  {cells.map((cell, idx) => {
                    const selected = draft === cell.iso;
                    const isToday = cell.iso === todayIso;
                    return (
                      <Button
                        key={idx}
                        variant={ selected ? 'primary' : isToday ? 'bordered' : 'text' }
                        type="button"
                        color={selected ? 'primary' : isToday ? 'primary' : 'white'}
                        size="icon-sm"
                        className={`!px-0 !py-2 rounded-xl ${
                          cell.currentMonth ? '' : 'opacity-50'
                        }`}
                        disabled={cell.disabled}
                        onClick={() => !cell.disabled && setDraft(cell.iso)}
                        aria-label={`${cell.day} ${MONTHS[parseIso(cell.iso)!.m]} ${parseIso(cell.iso)!.y}`}
                        aria-current={selected ? 'date' : undefined}
                      >
                        {cell.day}
                      </Button>
                    );
                  })}
                </div>
              </>
            )}

            {mode === 'months' && (
              <div className="grid grid-cols-3 gap-2">
                {MONTH_LABELS_SHORT.map((label, m) => {
                  const disabled = isMonthDisabled(viewYear, m);
                  const selected = draftP?.y === viewYear && draftP.m === m;
                  return (
                    <Button
                      key={label}
                      raw
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        if (disabled) return;
                        setViewMonth(m);
                        setMode('days');
                      }}
                      className={`flex h-11 items-center justify-center rounded-xl text-sm font-medium transition-colors ${
                        disabled
                          ? 'cursor-not-allowed text-text-tertiary opacity-40'
                          : selected
                            ? 'bg-blue-100 text-constant-white hover:bg-blue-80'
                            : 'text-text-primary hover:bg-bg-1'
                      }`}
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
            )}

            {mode === 'years' && (
              <div ref={yearsScrollRef} className="max-h-[260px] overflow-y-auto">
                <div className="grid grid-cols-4 gap-2">
                  {yearList.map((y) => {
                    const disabled = isYearDisabled(y);
                    const selected = draftP?.y === y;
                    const isCurrent = y === today.y;
                    return (
                      <Button
                        key={y}
                        raw
                        type="button"
                        disabled={disabled}
                        data-selected-year={selected || (!draftP && y === viewYear) ? 'true' : undefined}
                        onClick={() => {
                          if (disabled) return;
                          setViewYear(y);
                          setMode('months');
                        }}
                        className={`flex h-10 items-center justify-center rounded-xl text-sm font-medium transition-colors ${
                          disabled
                            ? 'cursor-not-allowed text-text-tertiary opacity-40'
                            : selected
                              ? 'bg-blue-100 text-constant-white hover:bg-blue-80'
                              : isCurrent
                                ? 'text-text-primary ring-1 ring-inset ring-blue-100 hover:bg-bg-1'
                                : 'text-text-primary hover:bg-bg-1'
                        }`}
                      >
                        {y}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 border-t border-neutral-10 px-5 py-4">
            {value ? (
              <Button
                type="button"
                variant="bordered"
                size="md"
                onClick={clear}
                className="rounded-xl py-2.5"
              >
                Clear
              </Button>
            ) : (
              <Button
                type="button"
                variant="bordered"
                size="md"
                onClick={onClose}
                className="rounded-xl py-2.5"
              >
                Cancel
              </Button>
            )}
            <Button
              type="button"
              variant="primary"
              size="md"
              disabled={!draft}
              onClick={apply}
              className="flex-1 rounded-xl"
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
